import { getConfig, klinikenApi } from "@/api";
import { Resurs } from "@/components/forms/reusables/resurserHelper";
import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

type State = BaseState & {
    resurser: Map<Resurs["id"], Resurs>;
};

const getDefaultState = (): State => ({
    resurser: new Map(),
    ...cloneDeep(baseState),
});

const moduleName = "resurser";
const baseUrl = "operation/resurser/";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        resurser: (state: State) => [...state.resurser.values()],
        resursSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({ ...props, state, collectionName: moduleName }),
        resursById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) return null;
                return state.resurser.get(id) ?? null;
            },
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());
                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addResurser: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                resurser = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                resurser: Resurs[];
                resetSubset?: boolean;
            }
        ) =>
            subsetSetter<State, Resurs>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: resurser,
            }),
        updateResurs: (
            state: State,
            {
                resurser,
                offset,
            }: {
                resurser: Resurs[];
                offset: number;
                subsetName: keyof Subsets;
            }
        ) =>
            subsetUpdateSetter<State, Resurs>({
                offset,
                state,
                collectionName: moduleName,
                items: resurser,
            }),
    },
    actions: {
        ...baseActions({ moduleName }),
        fetchResurser: async (
            { commit, state }: BaseActionsProps<State>,
            {
                filterParams = {},
                limit = 100,
                page = 1,
                forceRefresh = false,
            }: {
                filterParams?: FilterParams;
                limit?: number;
                page?: number;
                forceRefresh?: boolean;
            } = {}
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Resurs>({
                    filterParams,
                    state,
                    page,
                    limit,
                    forceRefresh,
                    collectionName: moduleName,
                    apiUrl: baseUrl,
                });
                if (results) commit("addResurser", { subsetName, offset, resurser: results });
                return { total, nextPage, subsetName, results };
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        createResurs: async ({ commit, state }: BaseActionsProps<State>, { resurs }: { resurs: Resurs }) => {
            try {
                const response = await klinikenApi.post(baseUrl, resurs, getConfig({}));
                commit("addResurser", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    resurser: [response.data],
                });
                return response;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        updateResurs: async ({ commit, state }: BaseActionsProps<State>, { resurs }: { resurs: Resurs }) => {
            try {
                const response = await klinikenApi.put(`${baseUrl}${resurs.id}/`, resurs, getConfig({}));
                commit("updateResurs", {
                    subsetName: "newlyUpdated",
                    resurser: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
                return response;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
    },
};
